import { fetchWeatherApi } from 'openmeteo';
import dayjs from 'dayjs';
import { ReadingSchema } from '../../../requests/api/apiTypes';

export type WeatherReportData = {
  windSpeed: number | null,
  windDirection: string | null,
  solarRadiation: number | null,
  humidity: number | null,
  temperature: number | null,
  maxTemperature: number | null,
  minTemperature: number | null,
  dailyRain: number | null,
  hourlyRain: number | null,
  eto: number | null,
};

export type WeatherForecastData = {
  day: string | null;
  date: string | null;
  weather: string;
  minTemperature: number | null;
  maxTemperature: number | null;
  RainMm: number | null;
  RainProbability: number | null;
  eto: number | null;
};

export type Forecast = {
  [key: number]: WeatherForecastData;
};

// URLs de las APIs
const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';

// Constantes
const WEATHER_TIMEZONE = 'America/Santiago';
const FORECAST_DAYS = 8;
const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sab'];

// Parámetros comunes para las consultas del clima
const COMMON_DAILY_PARAMS = [
  'weather_code',
  'temperature_2m_min',
  'temperature_2m_max',
  'precipitation_sum',
  'et0_fao_evapotranspiration',
];

const EXTENDED_DAILY_PARAMS = [
  ...COMMON_DAILY_PARAMS,
  'precipitation_probability_max',
  'uv_index_max',
];

// Utilidades

const roundToOneDecimal = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }
  return parseFloat(value.toFixed(1));
};

const getWeatherDescription = (code: number | null): string => {
  if (code === null) return 'N/A';

  const weatherMap: { [key: number]: string } = {
    0: 'Soleado',
    1: 'Nublado',
    2: 'Nublado',
    3: 'Nublado',
    45: 'Neblina',
    48: 'Neblina',
    51: 'Llovizna',
    53: 'Llovizna',
    55: 'Llovizna',
    56: 'Llovizna',
    57: 'Llovizna',
    61: 'Lluvioso',
    63: 'Lluvioso',
    65: 'Lluvioso',
    66: 'Lluvioso',
    67: 'Lluvioso',
    71: 'Nieve',
    73: 'Nieve',
    75: 'Nieve',
    77: 'Granizo',
    80: 'Chubascos',
    81: 'Chubascos',
    82: 'Chubascos',
    85: 'Nieve',
    86: 'Nieve',
    95: 'Tormenta',
  };

  return weatherMap[code] || 'N/A';
};

export const getWeatherEmoji = (weather: string): string => {
  const emojiMap: { [key: string]: string } = {
    Soleado: '☀️',
    Nublado: '☁️',
    Neblina: '🌫️',
    Llovizna: '🌦️',
    Lluvioso: '🌧️',
    Nieve: '❄️',
    Granizo: '🧊',
    Chubascos: '⛈️',
    Tormenta: '⚡',
    'N/A': '❓',
  };

  return emojiMap[weather] || '❓';
};

const formatDate = (date: Date): string => `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const formatDateString = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const processWeatherData = (
  weatherCodes: Float32Array | number[],
  minTemps: Float32Array | number[],
  maxTemps: Float32Array | number[],
  precipitations: Float32Array | number[],
  etoValues: Float32Array | number[],
  startDate: Date,
  includeRainProbability = false,
  rainProbabilities?: Float32Array | number[],
): { [key: string]: WeatherForecastData } => {
  const weatherData: { [key: string]: WeatherForecastData } = {};

  for (let i = 0; i < weatherCodes.length; i += 1) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);

    const dateString = formatDateString(currentDate);
    const day = DAYS_OF_WEEK[currentDate.getDay()];
    const formattedDate = formatDate(currentDate);

    weatherData[dateString] = {
      day,
      date: formattedDate,
      minTemperature: roundToOneDecimal(minTemps[i] ?? null),
      maxTemperature: roundToOneDecimal(maxTemps[i] ?? null),
      RainMm: roundToOneDecimal(precipitations[i] ?? null),
      RainProbability: includeRainProbability && rainProbabilities
        ? roundToOneDecimal(rainProbabilities[i] ?? null)
        : null,
      eto: roundToOneDecimal(etoValues[i] ?? null),
      weather: getWeatherDescription(weatherCodes[i] ?? null),
    };
  }

  return weatherData;
};

export const fetchWeatherForecast = async (
  localLatitude: number,
  localLongitude: number,
): Promise<Forecast> => {
  const params = {
    latitude: localLatitude,
    longitude: localLongitude,
    daily: EXTENDED_DAILY_PARAMS,
    timezone: WEATHER_TIMEZONE,
    forecast_days: FORECAST_DAYS,
  };

  const responses = await fetchWeatherApi(FORECAST_URL, params);
  const response = responses[0];
  const daily = response.daily();

  const forecast: Forecast = {};
  const today = new Date();

  for (let i = 0; i < FORECAST_DAYS; i += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    const day = DAYS_OF_WEEK[date.getDay()];
    const formattedDate = formatDate(date);

    forecast[i] = {
      day,
      date: formattedDate,
      minTemperature: roundToOneDecimal(daily?.variables(1)?.valuesArray()?.[i] ?? null),
      maxTemperature: roundToOneDecimal(daily?.variables(2)?.valuesArray()?.[i] ?? null),
      RainMm: roundToOneDecimal(daily?.variables(3)?.valuesArray()?.[i] ?? null),
      RainProbability: roundToOneDecimal(daily?.variables(5)?.valuesArray()?.[i] ?? null),
      eto: roundToOneDecimal(daily?.variables(4)?.valuesArray()?.[i] ?? null),
      weather: getWeatherDescription(daily?.variables(0)?.valuesArray()?.[i] ?? null),
    };
  }

  return forecast;
};

export const fetchWeatherHistoricalData = async (
  localLatitude: number,
  localLongitude: number,
  startDate: string,
  endDate: string,
): Promise<{ [key: string]: WeatherForecastData }> => {
  try {
    const start = dayjs(startDate).toDate();
    const end = dayjs(endDate).toDate();
    const timeDiff = end.getTime() - start.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;

    // Calcular cuántos días hacia atrás necesitamos
    const today = new Date();
    const startDateObj = new Date(startDate);
    const daysFromToday = Math.ceil(
      (today.getTime() - startDateObj.getTime()) / (1000 * 3600 * 24),
    );

    const params = {
      latitude: localLatitude,
      longitude: localLongitude,
      daily: EXTENDED_DAILY_PARAMS,
      timezone: WEATHER_TIMEZONE,
      past_days: Math.max(daysFromToday, 7), // Mínimo 7 días para asegurar que tenemos datos
      forecast_days: 0, // No necesitamos forecast
    };

    const responses = await fetchWeatherApi(FORECAST_URL, params);
    const response = responses[0];
    const daily = response.daily();

    const weatherCodes = daily?.variables(0)?.valuesArray() || [];
    const minTemps = daily?.variables(1)?.valuesArray() || [];
    const maxTemps = daily?.variables(2)?.valuesArray() || [];
    const precipitations = daily?.variables(3)?.valuesArray() || [];
    const etoValues = daily?.variables(4)?.valuesArray() || [];
    const rainProbabilities = daily?.variables(5)?.valuesArray() || [];

    // Filtrar solo los datos que necesitamos para el rango de fechas
    const startIndex = Math.max(0, weatherCodes.length - daysDiff);
    const limitedWeatherCodes = weatherCodes.slice(startIndex);
    const limitedMinTemps = minTemps.slice(startIndex);
    const limitedMaxTemps = maxTemps.slice(startIndex);
    const limitedPrecipitations = precipitations.slice(startIndex);
    const limitedEtoValues = etoValues.slice(startIndex);
    const limitedRainProbabilities = rainProbabilities.slice(startIndex);

    return processWeatherData(
      limitedWeatherCodes,
      limitedMinTemps,
      limitedMaxTemps,
      limitedPrecipitations,
      limitedEtoValues,
      start,
      true,
      limitedRainProbabilities,
    );
  } catch (error) {
    console.error('Error fetching historical weather data:', error);
    return {};
  }
};

export const fetchWeatherForecastWithPastDays = async (
  localLatitude: number,
  localLongitude: number,
  pastDays = 5,
): Promise<{ [key: string]: WeatherForecastData }> => {
  const params = {
    latitude: localLatitude,
    longitude: localLongitude,
    daily: EXTENDED_DAILY_PARAMS,
    timezone: WEATHER_TIMEZONE,
    past_days: pastDays,
    forecast_days: FORECAST_DAYS,
  };

  const responses = await fetchWeatherApi(FORECAST_URL, params);
  const response = responses[0];
  const daily = response.daily();

  const weatherCodes = daily?.variables(0)?.valuesArray() || [];
  const minTemps = daily?.variables(1)?.valuesArray() || [];
  const maxTemps = daily?.variables(2)?.valuesArray() || [];
  const precipitations = daily?.variables(3)?.valuesArray() || [];
  const etoValues = daily?.variables(4)?.valuesArray() || [];
  const rainProbabilities = daily?.variables(5)?.valuesArray() || [];

  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - pastDays);

  return processWeatherData(
    weatherCodes,
    minTemps,
    maxTemps,
    precipitations,
    etoValues,
    startDate,
    true,
    rainProbabilities,
  );
};

export const processWeatherReadingValue = (
  reading: ReadingSchema,
  processedData: WeatherReportData,
): void => {
  const value = parseFloat(reading.value);
  if (Number.isNaN(value)) return;

  switch (reading.sensorType) {
    case 'Wind Velocity':
      processedData.windSpeed = value;
      break;
    case 'Wind Speed':
      processedData.windSpeed = value;
      break;
    case 'Wind Direction':
      processedData.windDirection = reading.value;
      break;
    case 'Solar Radiation':
      processedData.solarRadiation = value;
      break;
    case 'Humidity':
      processedData.humidity = value;
      break;
    case 'Temperature':
      processedData.temperature = value;
      if (processedData.maxTemperature === null || value > processedData.maxTemperature) {
        processedData.maxTemperature = value;
      }
      if (processedData.minTemperature === null || value < processedData.minTemperature) {
        processedData.minTemperature = value;
      }
      break;
    case 'Rain':
      processedData.dailyRain = value;
      processedData.hourlyRain = value;
      break;
    default:
      break;
  }
};
